/**
 * Nutrition types for CIQUAL / OpenFoodFacts integration.
 * Captures fields needed for a future Nutri-Score pipeline.
 */

export type PreparationState = 'raw' | 'cooked' | 'other';

export type NutriScoreCategory =
  | 'general'
  | 'beverage'
  | 'fat'
  | 'cheese'
  | 'redMeat';

export type NutritionSource = 'ciqual' | 'openfoodfacts' | 'manual' | null;

export type NutritionMatchStatus =
  | 'auto'
  | 'reviewed'
  | 'manual'
  | 'none'
  | 'stale';

/** Per-100g macros copied onto the ingredient at match time. */
export interface NutritionPer100g {
  kcal: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  sugars_g: number | null;
  fat_g: number | null;
  saturatedFat_g: number | null;
  fiber_g: number | null;
  salt_g: number | null;
}

export interface IngredientNutrition {
  source: NutritionSource;
  ciqualCode: number | null;
  offBarcode: string | null;
  matchedName: string | null;
  matchScore: number | null;
  matchStatus: NutritionMatchStatus;
  /** If true, mass sync must never overwrite per100g values. */
  manualOverride: boolean;
  per100g: NutritionPer100g;
  fruitVegLegumePct: number | null;
  nutriScoreCategory: NutriScoreCategory | string | null;
  lastSyncedAt: string | null;
  lastSyncedSourceVersion: string | null;
}

export interface CiqualFoodNutrition {
  kcal: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  sugars_g: number | null;
  fat_g: number | null;
  saturatedFat_g: number | null;
  fiber_g: number | null;
  salt_g: number | null;
}

/** Firestore `ciqualFoods/{ciqualCode}` */
export interface CiqualFood {
  id: string;
  ciqualCode: number;
  nameRaw: string;
  baseName: string;
  qualifiers: string | null;
  preparationState: PreparationState;
  group: string;
  subgroup: string;
  subsubgroup: string | null;
  nutrition: CiqualFoodNutrition;
  fruitVegLegumePct: number;
  nutriScoreCategory: NutriScoreCategory;
  sourceVersion: string;
  importedAt: string;
  searchTokens: string[];
}

export interface CiqualMatchCandidate {
  ciqualCode: number;
  nameRaw: string;
  baseName: string;
  preparationState: PreparationState;
  group: string;
  score: number;
  nutrition: CiqualFoodNutrition;
  fruitVegLegumePct: number;
  nutriScoreCategory: NutriScoreCategory;
  sourceVersion: string;
}

export interface OffMatchCandidate {
  barcode: string;
  nameRaw: string;
  brand: string | null;
  score: number;
  nutrition: NutritionPer100g;
  source: 'openfoodfacts';
}

export function emptyNutritionPer100g(): NutritionPer100g {
  return {
    kcal: null,
    protein_g: null,
    carbs_g: null,
    sugars_g: null,
    fat_g: null,
    saturatedFat_g: null,
    fiber_g: null,
    salt_g: null,
  };
}

export function emptyIngredientNutrition(): IngredientNutrition {
  return {
    source: null,
    ciqualCode: null,
    offBarcode: null,
    matchedName: null,
    matchScore: null,
    matchStatus: 'none',
    manualOverride: false,
    per100g: emptyNutritionPer100g(),
    fruitVegLegumePct: null,
    nutriScoreCategory: null,
    lastSyncedAt: null,
    lastSyncedSourceVersion: null,
  };
}
